import { gql, TypedDocumentNode } from '@common/hooks';
import { OnDemandRateFields } from '@scheduling/graphql/fragments';
import {
  SimplifiedWorkforceGroupType,
  StaticPricingTier,
} from '@scheduling/graphql';
import { OrganizationUnitType } from '@institutions/graphql';

export const WorkforceGroupUnitRatesListQuery = gql`
  query WorkforceGroupUnitRatesList($input: WorkforceGroupUnitRatesListInput!) {
    workforceGroupUnitRatesList(input: $input) {
      collection {
        id
        organizationUnit {
          id
          name
          costCenter
          organizationBusiness {
            id
            name
          }
        }
        workforceGroup {
          id
          name
        }
        createdBy {
          id
          name
        }
        updatedAt
        lastUpdatedAt
        effectiveDate
        endDate
        pricingMode
        displayAsLumpSum
        priceRange
        minPrice
        maxPrice
        incentiveIncrement
        staticPricingTiers {
          id
          label
          payRate
          fillRateThreshold
        }
      }
      metadata {
        totalCount
      }
    }
  }
`;

// TODO(WORK-10927): split UnitRateType, whose mixed shapes break type inference
export type UnitRateType = {
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  effectiveDate: string;
  endDate: string | null;
  fridayCoreDayIncentiveRate: number;
  fridayCoreNightIncentiveRate: number;
  fridayFloatDayIncentiveRate: number;
  fridayFloatNightIncentiveRate: number;
  id: string;
  incentiveIncrement: number;
  mondayCoreDayIncentiveRate: number;
  mondayCoreNightIncentiveRate: number;
  mondayFloatDayIncentiveRate: number;
  mondayFloatNightIncentiveRate: number;
  organizationUnit: {
    id: string;
    name: string;
  };
  priceRange: number;
  saturdayCoreDayIncentiveRate: number;
  saturdayCoreNightIncentiveRate: number;
  saturdayFloatDayIncentiveRate: number;
  saturdayFloatNightIncentiveRate: number;
  sundayCoreDayIncentiveRate: number;
  sundayCoreNightIncentiveRate: number;
  sundayFloatDayIncentiveRate: number;
  sundayFloatNightIncentiveRate: number;
  thursdayCoreDayIncentiveRate: number;
  thursdayCoreNightIncentiveRate: number;
  thursdayFloatDayIncentiveRate: number;
  thursdayFloatNightIncentiveRate: number;
  tuesdayCoreDayIncentiveRate: number;
  tuesdayCoreNightIncentiveRate: number;
  tuesdayFloatDayIncentiveRate: number;
  tuesdayFloatNightIncentiveRate: number;
  pricingMode: 'DYNAMIC' | 'STATIC';
  displayAsLumpSum: boolean;
  staticPricingTiers?: {
    id: string;
    label: string;
    payRate: number;
    fillRateThreshold: number;
  }[];
  type: 'default' | 'variable' | 'static' | 'legacy' | 'empty';
  updatedAt: string;
  wednesdayCoreDayIncentiveRate: number;
  wednesdayCoreNightIncentiveRate: number;
  wednesdayFloatDayIncentiveRate: number;
  wednesdayFloatNightIncentiveRate: number;
  workforceGroup: {
    id: string;
    name: string;
  };
};

export type workforceGroupUnitRatesLogType = {
  effectiveRate: UnitRateType;
  noLongerInEffectRates: UnitRateType[];
  upcomingRates: UnitRateType[];
  organizationUnit: {
    id: string;
    name: string;
  };
  workforceGroup: {
    id: string;
    name: string;
  };
};

type WorkforceGroupUnitRatesLogQueryCgResponse = {
  workforceGroupUnitRatesLog: workforceGroupUnitRatesLogType;
};

type WorkforceGroupUnitRatesLogQueryVariables = {
  workforceGroupId: string;
  organizationUnitId: string;
  effectiveDate: string;
};

// TODO(WORK-3458): extract rate fields to a graphql fragment
export const WorkforceGroupUnitRatesLogQuery: TypedDocumentNode<
  WorkforceGroupUnitRatesLogQueryCgResponse,
  WorkforceGroupUnitRatesLogQueryVariables
> = gql`
  ${OnDemandRateFields}
  query WorkforceGroupUnitRatesLog(
    $workforceGroupId: ID!
    $organizationUnitId: ID!
    $effectiveDate: ISO8601Date!
  ) {
    workforceGroupUnitRatesLog(
      workforceGroupId: $workforceGroupId
      organizationUnitId: $organizationUnitId
      effectiveDate: $effectiveDate
    ) {
      organizationUnit {
        id
        name
      }
      workforceGroup {
        id
        name
      }
      effectiveRate {
        id
        ...OnDemandRateFields
      }
      upcomingRates {
        id
        ...OnDemandRateFields
      }
      noLongerInEffectRates {
        id
        ...OnDemandRateFields
      }
    }
  }
`;

export const WorksOrganizationUnitFilterDataQuery = gql`
  query WorksOrganizationUnits($input: OrganizationUnitsInput) {
    organizationUnits(input: $input) {
      id
      name
      costCenter
    }
  }
`;

export const WorksOrganizationBusinessFilterDataQuery = gql`
  query WorksOrganizationBusinesses {
    organizationBusinesses {
      id
      name
    }
  }
`;

export type WorkforceGroupUnitRatesVariables = {
  input: {
    filters: Record<string, string | string[]>;
    orders?: { [p: string]: 'descend' | 'ascend' };
    pagination?: { page: number; pageSize: number };
  };
};

type WorkforceGroupUnitRate = {
  id: string;
  organizationUnit: OrganizationUnitType;
  workforceGroup: SimplifiedWorkforceGroupType;
  updatedAt: string;
  lastUpdatedAt: string;
  effectiveDate: string;
  endDate: string | null;
  pricingMode: 'DYNAMIC' | 'STATIC';
  displayAsLumpSum: boolean;
  minPrice: number;
  maxPrice: number;
  priceRange: number;
  incentiveIncrement: number;
  createdBy: { id: string; name: string };
  staticPricingTiers?: StaticPricingTier[];
};

export type WorkforceGroupUnitRatesResponse = {
  workforceGroupUnitRatesList: {
    collection: WorkforceGroupUnitRate[];
    metadata: {
      totalCount: number;
    };
  };
};
